<template lang="pug">
  div(
    class="block-document-share-participant-item"
    :class="itemClasses"
  )
    div(
      v-if="showDots"
      class="block-document-share-participant-item__dots draggable"
    )
      e-svg-icon(
        name="six-dots-vertical"
        size="lg"
      )
    div(
      v-if="showNumber"
      class="block-document-share-participant-item__number"
      :class="{ disabled: isDisabled }"
    ) {{ number }}
    div(
      class="block-document-share-participant-item__content"
      :class="{ disabled: isDisabled }"
    )
      div(class="text-14px dark") {{ displayName }}
      div(
        v-if="edrpouDisplay"
        class="block-document-share-participant-item__edrpou text-14px light"
      ) {{ edrpouDisplay }}
    div(class="d-flex align-center")
      v-btn(
        v-if="showDeleteButton"
        icon
        data-test="document-share-pro-delete-user-btn"
        @click="deleteParticipant"
      )
        e-svg-icon danger-trash
      div(
        class="block-document-share-participant-item__action-select-wrapper"
        :class="{ disabled: disabled }"
      ) 
        e-unified-select(
          v-if="showActionSelect"
          :value="currentAction"
          :items="actions"
          :loading="loading"
          :disabled="disabled"
          custom-class="block-document-share-participant-item__action-select"
          :data-test="dataTest"
          @input="handleActionChange"
        )
    div(
      v-if="showActionLabel"
      class="block-document-share-participant-item__actions mr-6"
    )
      span(class="text-14px dark medium") {{ actionLabel }}
</template>

<script>
import EUnifiedSelect from '~/components/elements/selections/e-unified-select'
import participants from '~/modules/documents/mixins/participants'

export default {
  name: 'BlockDocumentShareParticipantItem',
  components: {
    EUnifiedSelect
  },
  mixins: [participants],
  props: {
    participant: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      default: null
    },
    isDragMode: {
      type: Boolean,
      default: false
    },
    showNumber: {
      type: Boolean,
      default: true
    },
    actions: {
      type: Array,
      default: () => []
    },
    currentAction: {
      type: Object,
      default: null
    },
    actionLabel: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    dataTest: {
      type: String,
      default: ''
    }
  },
  computed: {
    isOwner() {
      return this.participant.isOwner || false
    },
    emailOrEdrpou() {
      return this.participant.emailOrEdrpou || ''
    },
    isEdrpou() {
      return /^\d{8,10}$/.test(this.emailOrEdrpou)
    },
    displayName() {
      if (this.participant.isOwner) {
        return this.$t('You (Owner)')
      }
      if (this.participant.displayName) {
        return this.participant.displayName
      }
      if (this.isEdrpou) {
        return `${this.$t('Organization')} ${this.emailOrEdrpou}`
      }
      return this.emailOrEdrpou || this.$t('New participant')
    },
    edrpouDisplay() {
      return this.emailOrEdrpou && this.isEdrpou
        ? `${this.$t('EDRPOU')} ${this.emailOrEdrpou}`
        : null
    },
    number() {
      return this.index !== null ? this.index + 1 : null
    },
    showDots() {
      return this.isDragMode && !this.isOwner && !this.disabled
    },
    showActionSelect() {
      return !this.isDragMode && !this.isOwner && this.actions.length > 0
    },
    showActionLabel() {
      return this.isDragMode && !this.isOwner
    },
    showDeleteButton() {
      return !this.isDragMode && !this.isOwner && this.canDeleteParticipant(this.participant)
    },
    isDisabled() {
      return (this.isOwner && this.isDragMode) || this.disabled
    },
    itemClasses() {
      return {
        'block-document-share-participant-item--drag-mode': this.isDragMode,
        'draggable-enabled': !this.isOwner && this.isDragMode && !this.disabled
      }
    }
  },
  methods: {
    handleActionChange(value) {
      this.$emit('action-change', value)
    },
    deleteParticipant() {
      this.$emit('delete-participant', this.participant)
    }
  }
}
</script>

<style scoped lang="scss">
.block-document-share-participant-item {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
  padding: 8px 0;
  &--drag-mode {
    padding: 8px 8px 8px 0;
    border-radius: 16px;
    height: 64px;
    border: 1px solid $border-disabled;
    background: $background-gray;
  }
  &__number {
    display: flex;
    width: 48px;
    height: 48px;
    padding: 12px;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border-radius: $btn-border-radius;
    background: $background-gray;

    &.disabled {
      opacity: 0.6;
    }
  }
  &__dots {
    width: 48px;
    height: 48px;
    background: $background-gray;
    border-radius: $btn-border-radius 0 0 $btn-border-radius;
    border: 1px solid $background-gray;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    cursor: grab;
    &:active {
      cursor: grabbing;
    }
  }
  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;

    &.disabled {
      opacity: 0.6;
    }
  }
  &__edrpou {
    overflow: hidden;
    color: $border-primary;
    text-overflow: ellipsis;
  }
  &__action-select-wrapper {
    &.disabled {
      opacity: 0.6;
    }
  }
  &__action-select {
    min-width: 120px;
    max-width: 150px;
    &.disabled {
      opacity: 0.6;
    }
  }
  &__actions {
    display: flex;
    align-items: center;
  }
  &--active {
    border: 2px dashed $primary !important;
    background: $secondary !important;
    .block-document-share-participant-item__dots {
      background: rgba($primary, 0.16);
      border: 1px solid rgba($primary, 0.16);
    }
  }
  &--chosen {
    border: 2px solid $primary !important;
    background: rgba($primary, 0.1) !important;
    transform: scale(1.02);
    box-shadow: 0 4px 8px rgba($black, 0.1);
    z-index: 999;
  }
  &.sortable-chosen {
    border-radius: 16px;
    border: 1px dashed $primary;
    background: rgba($primary, 0.04);
  }
}
</style>
